import { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileText, X, Loader2, ImageIcon } from "lucide-react";

/**
 * FileUpload — single-file dropzone that uploads to /api/files/upload.
 * Calls onUploaded({id, url, filename, content_type}) once the file lands.
 *
 * If `value` is provided (file_id) it shows a thumbnail / preview.
 */
export default function FileUpload({
    purpose,
    value,
    onUploaded,
    onCleared,
    label,
    accept = "image/*,application/pdf",
    testid,
}) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [meta, setMeta] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");

    useEffect(() => {
        if (!value) {
            setMeta(null);
            setPreviewUrl("");
            return;
        }
        if (meta && meta.id === value) return;
        // hydrate by listing files (cheaper than a one-shot fetch w/ auth headers)
        let cancelled = false;
        api.get("/files/me/list").then(({ data }) => {
            if (cancelled) return;
            const found = (data || []).find((f) => f.id === value);
            if (found) {
                setMeta(found);
                if ((found.content_type || "").startsWith("image/")) {
                    const token = localStorage.getItem("token");
                    setPreviewUrl(`${API}/files/${found.id}?auth=${encodeURIComponent(token || "")}`);
                }
            }
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [value]);

    const handleFile = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("purpose", purpose);
            const { data } = await api.post("/files/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setMeta(data);
            if ((data.content_type || "").startsWith("image/")) {
                const token = localStorage.getItem("token");
                setPreviewUrl(`${API}/files/${data.id}?auth=${encodeURIComponent(token || "")}`);
            } else {
                setPreviewUrl("");
            }
            onUploaded?.(data);
            toast.success("File uploaded");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const onClear = async () => {
        if (!meta) {
            onCleared?.();
            return;
        }
        try {
            await api.delete(`/files/${meta.id}`);
        } catch {}
        setMeta(null);
        setPreviewUrl("");
        onCleared?.();
    };

    return (
        <div data-testid={testid || `file-upload-${purpose}`}>
            {label && (
                <div className="overline mb-2 block">{label}</div>
            )}
            {!meta ? (
                <label
                    className="block border border-dashed border-white/15 hover:border-primary/60 transition-colors rounded-sm p-6 cursor-pointer bg-input/40"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        accept={accept}
                        className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        data-testid={`${testid || `file-upload-${purpose}`}-input`}
                    />
                    <div className="flex items-center gap-3 text-muted-foreground">
                        {uploading ? (
                            <Loader2 size={18} className="animate-spin gold-text" />
                        ) : (
                            <UploadCloud size={18} className="gold-text" strokeWidth={1.5} />
                        )}
                        <div className="text-xs">
                            <div className="text-foreground tracking-[0.15em] uppercase">
                                {uploading ? "Uploading..." : "Click or drop to upload"}
                            </div>
                            <div className="opacity-70 mt-1">PNG, JPG, HEIC or PDF · max 8MB</div>
                        </div>
                    </div>
                </label>
            ) : (
                <div className="flex items-center gap-4 p-3 bg-input border border-white/10 rounded-sm">
                    {previewUrl ? (
                        <img src={previewUrl} alt="preview" className="w-14 h-14 object-cover rounded-sm" />
                    ) : (
                        <div className="w-14 h-14 grid place-items-center bg-card border border-white/10 rounded-sm gold-text">
                            {(meta.content_type || "").includes("pdf") ? <FileText size={20} strokeWidth={1.5} /> : <ImageIcon size={20} strokeWidth={1.5} />}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{meta.original_filename || meta.filename || "uploaded"}</div>
                        <div className="text-[11px] text-muted-foreground">{Math.round((meta.size || 0) / 1024)} KB · {meta.content_type}</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClear}
                        className="p-2 text-muted-foreground hover:text-red-400 transition-colors"
                        aria-label="remove"
                        data-testid={`${testid || `file-upload-${purpose}`}-clear`}
                    >
                        <X size={14} strokeWidth={1.5} />
                    </button>
                </div>
            )}
        </div>
    );
}

/** AuthImage — render an authenticated image using ?auth=token query param */
export function AuthImage({ fileId, alt = "", className = "" }) {
    if (!fileId) return null;
    const token = localStorage.getItem("token");
    return (
        <img
            src={`${API}/files/${fileId}?auth=${encodeURIComponent(token || "")}`}
            alt={alt}
            className={className}
        />
    );
}
